import React, { useEffect } from "react";

import Images from "assets/images";
import Input from "components/inputs/Input";
import Spacer from "components/Spacer/Spacer";
import Button from "components/buttons/Button";

import { loginAction } from "api/actions/AuthActions";

import { useApiMutation } from "hooks/useApiMutation";
import { Formik, Form } from "formik";
import { useDispatch } from "react-redux";
import { LoginSchema } from "helpers/validationSchema";
import { signinUser } from "reducers/currentUserReducer";
import { useHistory, useLocation } from "react-router-dom";
import useToastify from "hooks/useToastify";
import FormikErrorFocus from "formik-error-focus";
import { setUserProp, trackEvent } from "helpers/analytics";
import Password from "components/inputs/Password";
import TextDivider from "components/Divider/TextDivider";
import GoogleLogin from "./GoogleLogin";
import AuthStepper from "components/authStepper/AuthStepper";

function LoginPage() {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { state } = location;

  useEffect(() => {
    let robotsMeta = document.querySelector('meta[name="robots"]');
    let createdMeta = false;
    const previousContent = robotsMeta?.getAttribute("content");

    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
      createdMeta = true;
    }

    robotsMeta.setAttribute("content", "noindex, nofollow");

    return () => {
      if (!robotsMeta) return;

      if (createdMeta) {
        robotsMeta.remove();
      } else if (previousContent) {
        robotsMeta.setAttribute("content", previousContent);
      } else {
        robotsMeta.removeAttribute("content");
      }
    };
  }, []);

  const { showAlert } = useToastify();
  const { mutate: callLoginApi, loading } = useApiMutation(loginAction);

  async function onFormSubmit(values) {
    try {
      const result = await callLoginApi({
        email: values.email,
        password: values.password,
      });
      if (result.error || result.payload.error || result.payload.error_msg) {
        errorHandler(result.payload.error || result.payload.error_msg);
      } else {
        successHandler(result);
      }
    } catch (err) {
      errorHandler(err);
    }
  }

  function successHandler(result) {
    dispatch(
      signinUser({
        userToken: result.payload.auth_token,
        userId: result.payload.id,
      })
    );
    setUserProp({ user_id: result.payload.id });
    if (
      state?.from?.pathname &&
      state?.from?.pathname !== "/home/profile/settings" // Handle logged out use case
    ) {
      history.replace(state?.from?.pathname);
    } else {
      history.replace("/home");
    }
  }

  function errorHandler(message) {
    showAlert(message, "error");
  }

  return (
    <div className="flex flex-col justify-center h-screen bg-veryLightBlue md:bg-gradient-to-b from-blue-300 to-blue-500">
      <div className="w-full h-full mx-auto overflow-y-auto md:h-auto md:max-w-4xl bg-blue md:bg-white md:p-10 login-card md:rounded-xl">
        <Formik
          initialValues={{
            email: "",
            password: "",
          }}
          validationSchema={LoginSchema}
          onSubmit={onFormSubmit}
        >
          {({ dirty, isValid }) => (
            <Form className="px-12 py-12 pb-32 md:px-0 md:py-0">
              <p
                style={{ lineHeight: "2.8rem" }}
                className="hidden mb-12 text-3xl font-medium text-white"
              >
                Please enter your credentials to access your visits
              </p>
              <div className="p-12 bg-white md:p-0 rounded-3xl required-label">
                <div className="flex flex-col items-center justify-center pb-8 md:pb-12">
                  <img
                    className="object-contain h-28 w-28"
                    src={Images.logoVerticalNaming}
                    alt="logo"
                  />
                  <p className="mt-6 text-3xl md:text-4xl font-medium leading-8 md:mt-8 md:font-bold text-black text-center">
                    Doctors anytime, anywhere
                  </p>
                  <p className="px-4 pt-1 pb-0 text-xl font-medium text-center text-themeBlack md:px-0">
                    Please enter your credentials to access your visits
                  </p>
                </div>
                <div className="w-full mb-8 md:mb-10">
                  <AuthStepper activeStepIndex={0} />
                </div>
                <Input
                  name="email"
                  type="email"
                  label="Email"
                  id="email"
                  placeholder="johndoe@xyz.com"
                />
                <Spacer height={16} />
                <Password
                  name="password"
                  type="password"
                  label="Password"
                  id="password"
                  placeholder="Minimum 8 characters"
                  autoComplete="on"
                />
                <div
                  className="flex flex-row justify-end mt-4 text-2xl font-medium cursor-pointer text-secondaryBlue right"
                  href="/forgot-password"
                >
                  <a className="no-underline" href="/forgot-password">
                    Forgot Password
                  </a>
                </div>
                <Spacer height={24} />
                <Button
                  type="submit"
                  label={"Login"}
                  loading={loading}
                  disabled={loading || !(dirty && isValid)}
                  className={"h-18 mb-8"}
                />
                <TextDivider />
                <div className="flex flex-col md:flex-row justify-center items-center md:space-x-4">
                  <a href="/phone" className="no-underline flex-1 w-full">
                    <Button
                      outlined={true}
                      onClick={() => trackEvent("phone_login")}
                      className="h-18 my-8 text-[14px] md:text-2.5xl"
                      type="button"
                      label="Continue with Phone Number"
                    />
                  </a>
                  <GoogleLogin source="login"/>
                </div>
                <div className="flex flex-col items-center justify-center ">
                  <a
                    className="hidden mt-8 text-2xl font-medium underline cursor-pointer text-indigo"
                    href="/forgot-password"
                  >
                    Forgot Password
                  </a>
                  <Spacer height={14} />
                  <p className="text-2xl font-light leading-8 text-center text-indigo">
                    Don’t have an account yet?{" "}
                    <a
                      className="text-2xl font-medium no-underline cursor-pointer text-orange"
                      href="/signup"
                    >
                      Sign up
                    </a>
                  </p>
                </div>
                <FormikErrorFocus
                  align={"bottom"}
                  ease={"linear"}
                  duration={500}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default LoginPage;
